// Collaborative Markdown editor: Y.Text("md") is the single source of truth; Rich (live preview)
// and Source modes are just different decorations over the same text.
import { defaultKeymap, indentWithTab } from "@codemirror/commands";
import { markdown, markdownLanguage } from "@codemirror/lang-markdown";
import { codeFolding, foldKeymap, HighlightStyle, syntaxHighlighting } from "@codemirror/language";
import { searchKeymap } from "@codemirror/search";
import { Compartment, EditorState, type Extension } from "@codemirror/state";
import {
  drawSelection,
  EditorView,
  keymap,
  placeholder as placeholderExt,
} from "@codemirror/view";
import { tags } from "@lezer/highlight";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { yCollab, yUndoManagerKeymap } from "y-codemirror.next";
import { Awareness } from "y-protocols/awareness";
import { WebsocketProvider } from "y-websocket";
import * as Y from "yjs";
import { api, documentRoom, documentsServerUrl, errorMessage } from "../api";
import { taskAutocomplete } from "./autocomplete";
import { activeFormats, currentHeading, type FormatName } from "./commands";
import { editorContext, type EditorContextData } from "./context";
import { anchorGuard } from "./anchors";
import { livePreview, refreshDecorations, sourceMode } from "./livePreview";
import { taskFoldService } from "./taskBlocks";
import { EditorToolbar, formattingKeymap } from "./Toolbar";

export type EditorMode = "rich" | "source";
export type ConnState =
  | "connecting"
  | "live"
  | "reconnecting"
  | "http"
  | "saving"
  | "saved"
  | "error"
  | "local";

const MODE_KEY = "tia.editor.mode";
const REMOTE = Symbol("remote-load");
const SAVE_DEBOUNCE_MS = 1000;
const WS_INITIAL_TIMEOUT_MS = 5000;
const WS_DROP_TIMEOUT_MS = 8000;
const HTTP_POLL_MS = 20000;

function loadMode(): EditorMode {
  try {
    return localStorage.getItem(MODE_KEY) === "source" ? "source" : "rich";
  } catch {
    return "rich";
  }
}

const highlight = HighlightStyle.define([
  { tag: tags.heading1, class: "tok-h1" },
  { tag: tags.heading2, class: "tok-h2" },
  { tag: [tags.heading3, tags.heading4, tags.heading5, tags.heading6], class: "tok-h" },
  { tag: tags.emphasis, class: "tok-em" },
  { tag: tags.strong, class: "tok-strong" },
  { tag: tags.strikethrough, class: "tok-strike" },
  { tag: tags.link, class: "tok-link" },
  { tag: tags.url, class: "tok-url" },
  { tag: tags.monospace, class: "tok-mono" },
  { tag: tags.quote, class: "tok-quote" },
  { tag: [tags.processingInstruction, tags.meta, tags.contentSeparator], class: "tok-meta" },
]);

/** Replace ytext content with `next` using a minimal middle splice (keeps cursors stable). */
function applyText(ydoc: Y.Doc, ytext: Y.Text, next: string): void {
  const cur = ytext.toString();
  if (cur === next) return;
  let start = 0;
  const max = Math.min(cur.length, next.length);
  while (start < max && cur.charCodeAt(start) === next.charCodeAt(start)) start++;
  let endCur = cur.length;
  let endNext = next.length;
  while (endCur > start && endNext > start && cur.charCodeAt(endCur - 1) === next.charCodeAt(endNext - 1)) {
    endCur--;
    endNext--;
  }
  ydoc.transact(() => {
    if (endCur > start) ytext.delete(start, endCur - start);
    if (endNext > start) ytext.insert(start, next.slice(start, endNext));
  }, REMOTE);
}

export interface MarkdownEditorProps {
  /** Document id; null → detached mode (initialMarkdown + onDetachedSave). */
  docId: string | null;
  initialMarkdown?: string;
  onDetachedSave?: (markdown: string) => Promise<void>;
  context: EditorContextData;
  user?: { name: string; color?: string };
  placeholder?: string;
  ariaLabel: string;
  className?: string;
  autoFocus?: boolean;
  /** Bump to ask a non-live (HTTP fallback / detached) editor to reload the document. */
  externalVersion?: number;
}

const ANCHOR_IN_TEXT = /\s\^([a-z0-9]{4,12})\s*$/gm;

/** Opens an item in the centered modal over the current page (nesting inside an open modal). */
function useOpenItem(): (itemId: string) => void {
  const navigate = useNavigate();
  const location = useLocation();
  const locRef = useRef(location);
  locRef.current = location;
  return useCallback(
    (itemId: string) => {
      const loc = locRef.current;
      const st = loc.state as { background?: unknown; modalDepth?: number } | null;
      if (st?.background) navigate(`/items/${itemId}`, { state: { background: st.background, modalDepth: (st.modalDepth ?? 1) + 1 } });
      else navigate(`/items/${itemId}`, { state: { background: loc } });
    },
    [navigate],
  );
}

const STATE_LABEL: Record<ConnState, string> = {
  connecting: "Connecting…",
  live: "Live",
  reconnecting: "Reconnecting…",
  http: "Offline mode (HTTP)",
  saving: "Saving…",
  saved: "Saved",
  error: "Save failed",
  local: "Draft",
};

export function MarkdownEditor(props: MarkdownEditorProps) {
  const { docId, initialMarkdown, onDetachedSave, context, user, placeholder, ariaLabel, className, autoFocus, externalVersion } =
    props;
  const openItem = useOpenItem();
  const openItemRef = useRef(openItem);
  openItemRef.current = openItem;
  /** anchor → item id; null = endpoint unsupported (widget hidden). */
  const anchorsRef = useRef<Map<string, string> | undefined>(undefined);
  const reloadRef = useRef<(() => void) | null>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const modeCompartment = useRef(new Compartment());
  const ctxRef = useRef(context);
  ctxRef.current = context;
  const saveRef = useRef(onDetachedSave);
  saveRef.current = onDetachedSave;
  const initialRef = useRef(initialMarkdown ?? "");
  initialRef.current = initialMarkdown ?? "";

  const [mode, setMode] = useState<EditorMode>(loadMode);
  const modeRef = useRef(mode);
  const [conn, setConn] = useState<ConnState>(docId ? "connecting" : "local");
  const [loading, setLoading] = useState<boolean>(!!docId);
  const [lastError, setLastError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [fmt, setFmt] = useState<{ key: string; active: Set<FormatName>; heading: number }>({
    key: "",
    active: new Set(),
    heading: 0,
  });
  const fmtKeyRef = useRef("");

  const applyMode = useCallback((m: EditorMode) => {
    modeRef.current = m;
    setMode(m);
    try {
      localStorage.setItem(MODE_KEY, m);
    } catch {
      /* ignore */
    }
    viewRef.current?.dispatch({
      effects: modeCompartment.current.reconfigure(m === "rich" ? livePreview() : sourceMode()),
    });
  }, []);

  const toggleMode = useCallback(() => {
    applyMode(modeRef.current === "rich" ? "source" : "rich");
    viewRef.current?.focus();
  }, [applyMode]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let disposed = false;
    const ydoc = new Y.Doc();
    const ytext = ydoc.getText("md");
    const undoManager = new Y.UndoManager(ytext);
    const awareness = new Awareness(ydoc);
    if (user) {
      awareness.setLocalStateField("user", { name: user.name, color: user.color ?? "#4f6bed", colorLight: "#4f6bed33" });
    }

    let provider: WebsocketProvider | null = null;
    let everSynced = false;
    let fallback = false;
    let dirty = false; // local edits not yet saved via HTTP
    let editedWhileDisconnected = false;
    let saveTimer: number | null = null;
    let pollTimer: number | null = null;
    let wsTimer: number | null = null;
    let pendingRemote: string | null = null;
    let saving = false;

    const view = new EditorView({
      parent: host,
      state: EditorState.create({
        doc: "",
        extensions: buildExtensions(),
      }),
    });
    viewRef.current = view;

    function buildExtensions(): Extension[] {
      return [
        EditorView.lineWrapping,
        drawSelection(),
        keymap.of([
          { key: "Mod-e", preventDefault: true, run: () => (toggleMode(), true) },
          ...yUndoManagerKeymap,
          ...formattingKeymap(),
          indentWithTab,
          ...searchKeymap,
          ...foldKeymap,
          ...defaultKeymap,
        ]),
        markdown({ base: markdownLanguage, completeHTMLTags: false }),
        syntaxHighlighting(highlight),
        codeFolding({ placeholderText: "…" }),
        taskFoldService,
        modeCompartment.current.of(modeRef.current === "rich" ? livePreview() : sourceMode()),
        editorContext.of(() => ({ ...ctxRef.current, anchors: anchorsRef.current, openItem: (id) => openItemRef.current(id) })),
        taskAutocomplete(),
        anchorGuard(),
        yCollab(ytext, awareness, { undoManager }),
        placeholderExt(placeholder ?? "Write in Markdown… (- [ ] creates a task)"),
        EditorView.contentAttributes.of({ "aria-label": ariaLabel, "aria-multiline": "true" }),
        EditorView.updateListener.of((u) => {
          if (!u.docChanged && !u.selectionSet && !u.focusChanged) return;
          const active = activeFormats(u.state);
          const heading = currentHeading(u.state);
          const key = `${[...active].sort().join(",")}|${heading}`;
          if (key !== fmtKeyRef.current) {
            fmtKeyRef.current = key;
            setFmt({ key, active, heading });
          }
        }),
        EditorView.domEventHandlers({
          blur: () => {
            window.setTimeout(() => {
              if (!disposed && pendingRemote !== null && !dirty && !view.hasFocus) {
                applyText(ydoc, ytext, pendingRemote);
                pendingRemote = null;
              }
            }, 0);
            return false;
          },
        }),
      ];
    }

    // --- Anchor → item resolution for "open task" links (ASSUMED endpoint; hidden on 404) --------
    let anchorsSupported = !!docId;
    let anchorTimer: number | null = null;
    let anchorBusy = false;
    anchorsRef.current = undefined;
    const fetchAnchors = async () => {
      if (!docId || !anchorsSupported || anchorBusy) return;
      anchorBusy = true;
      try {
        const list = await api.documentAnchors(docId);
        if (disposed) return;
        if (list === null) {
          anchorsSupported = false;
          anchorsRef.current = undefined;
        } else {
          anchorsRef.current = new Map(list.map((a) => [a.anchor, a.item_id]));
        }
        view.dispatch({ effects: refreshDecorations.of(null) });
      } catch {
        /* transient: try again on the next change */
      } finally {
        anchorBusy = false;
      }
    };
    const scheduleAnchors = () => {
      if (!anchorsSupported) return;
      if (anchorTimer !== null) window.clearTimeout(anchorTimer);
      anchorTimer = window.setTimeout(() => {
        anchorTimer = null;
        const known = anchorsRef.current;
        const text = ytext.toString();
        const missing = [...text.matchAll(ANCHOR_IN_TEXT)].some((m) => !known?.has(m[1]));
        if (missing) void fetchAnchors();
      }, 1500);
    };
    const onAnyChange = () => scheduleAnchors();
    ytext.observe(onAnyChange);

    // --- HTTP save path (fallback + detached) ---------------------------------
    const flush = async () => {
      saveTimer = null;
      if (saving) {
        scheduleSave();
        return;
      }
      const sent = ytext.toString();
      dirty = false;
      saving = true;
      setConn("saving");
      try {
        if (!docId) {
          await saveRef.current?.(sent);
          if (!disposed) setConn("saved");
        } else {
          const res = await api.putDocument(docId, sent);
          if (disposed) return;
          setConn("saved");
          setLastError(null);
          if (res.markdown !== sent) {
            if (!dirty && !view.hasFocus) applyText(ydoc, ytext, res.markdown);
            else if (!dirty) pendingRemote = res.markdown;
          }
        }
      } catch (e) {
        if (disposed) return;
        dirty = true;
        setConn("error");
        setLastError(errorMessage(e));
      } finally {
        saving = false;
      }
    };

    function scheduleSave() {
      if (saveTimer !== null) window.clearTimeout(saveTimer);
      saveTimer = window.setTimeout(() => void flush(), SAVE_DEBOUNCE_MS);
    }

    const onLocalChange = (_e: Y.YTextEvent, tr: Y.Transaction) => {
      if (tr.origin === REMOTE) return;
      if (!fallback && docId) {
        if (provider && !provider.wsconnected) editedWhileDisconnected = true;
        return;
      }
      // fallback (HTTP) or detached mode: persist local edits
      if (!tr.local) return;
      dirty = true;
      pendingRemote = null;
      scheduleSave();
    };
    ytext.observe(onLocalChange);

    async function loadViaHttp() {
      if (!docId) return;
      try {
        const doc = await api.document(docId);
        if (disposed) return;
        if (!dirty && !view.hasFocus) applyText(ydoc, ytext, doc.markdown);
        else if (!dirty) pendingRemote = doc.markdown;
        setLastError(null);
        scheduleAnchors();
      } catch (e) {
        if (!disposed) setLastError(errorMessage(e));
      } finally {
        if (!disposed) setLoading(false);
      }
    }

    function enterFallback() {
      if (fallback || disposed || !docId) return;
      fallback = true;
      if (wsTimer !== null) window.clearTimeout(wsTimer);
      provider?.destroy();
      provider = null;
      setConn("http");
      if (!everSynced) {
        void loadViaHttp();
      } else if (editedWhileDisconnected) {
        dirty = true;
        scheduleSave();
      }
      pollTimer = window.setInterval(() => {
        if (!dirty && !saving && !view.hasFocus) void loadViaHttp();
      }, HTTP_POLL_MS);
    }

    if (docId) {
      provider = new WebsocketProvider(documentsServerUrl(), documentRoom(docId), ydoc, {
        awareness,
        disableBc: true,
        maxBackoffTime: 4000,
      });
      wsTimer = window.setTimeout(() => {
        if (!everSynced) enterFallback();
      }, WS_INITIAL_TIMEOUT_MS);
      provider.on("sync", (isSynced: boolean) => {
        if (disposed || !isSynced) return;
        everSynced = true;
        editedWhileDisconnected = false;
        scheduleAnchors();
        if (wsTimer !== null) window.clearTimeout(wsTimer);
        setConn("live");
        setLoading(false);
      });
      provider.on("status", ({ status }: { status: string }) => {
        if (disposed || fallback) return;
        if (status === "connected") {
          if (wsTimer !== null && everSynced) window.clearTimeout(wsTimer);
          if (everSynced) setConn("live");
        } else if (status === "disconnected" && everSynced) {
          setConn("reconnecting");
          if (wsTimer !== null) window.clearTimeout(wsTimer);
          wsTimer = window.setTimeout(() => {
            if (provider && !provider.wsconnected) enterFallback();
          }, WS_DROP_TIMEOUT_MS);
        }
      });
    } else {
      applyText(ydoc, ytext, initialRef.current);
      undoManager.clear();
      setConn("local");
      setLoading(false);
    }

    if (autoFocus) view.focus();
    reloadRef.current = () => {
      if (fallback && !dirty && !saving) void loadViaHttp();
    };

    return () => {
      disposed = true;
      reloadRef.current = null;
      if (anchorTimer !== null) window.clearTimeout(anchorTimer);
      ytext.unobserve(onAnyChange);
      // Flush pending HTTP save on unmount.
      if (saveTimer !== null) {
        window.clearTimeout(saveTimer);
        if (dirty) void flush();
      }
      if (pollTimer !== null) window.clearInterval(pollTimer);
      if (wsTimer !== null) window.clearTimeout(wsTimer);
      ytext.unobserve(onLocalChange);
      provider?.destroy();
      awareness.destroy();
      view.destroy();
      viewRef.current = null;
      ydoc.destroy();
    };
    // Rebuild only when the document changes; other props are read through refs.
  }, [docId, reloadKey]);

  useEffect(() => {
    if (externalVersion) reloadRef.current?.();
  }, [externalVersion]);

  const stateClass =
    conn === "live" || conn === "saved" || conn === "local"
      ? "ok"
      : conn === "error"
        ? "err"
        : conn === "http"
          ? "warn"
          : "busy";

  return (
    <div className={`md-editor ${className ?? ""}`} data-mode={mode}>
      <EditorToolbar
        getView={() => viewRef.current}
        active={fmt.active}
        heading={fmt.heading}
        modeSwitch={
          <div className="seg" role="group" aria-label="Editor mode">
            <button
              type="button"
              className={mode === "rich" ? "active" : ""}
              aria-pressed={mode === "rich"}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyMode("rich")}
              title="Rich mode (Ctrl/Cmd+E)"
            >
              Rich
            </button>
            <button
              type="button"
              className={mode === "source" ? "active" : ""}
              aria-pressed={mode === "source"}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyMode("source")}
              title="Source mode (Ctrl/Cmd+E)"
            >
              Source
            </button>
          </div>
        }
      >
        <span className={`conn-pill ${stateClass}`} role="status" title={lastError ?? STATE_LABEL[conn]}>
          <span className="dot" aria-hidden="true" />
          <span className="conn-text">{STATE_LABEL[conn]}</span>
        </span>
        {conn === "http" && (
          <button type="button" className="btn-link small" onClick={() => setReloadKey((k) => k + 1)}>
            Retry live
          </button>
        )}
      </EditorToolbar>
      <div className="md-editor-host" ref={hostRef} />
      {loading && <div className="md-editor-loading">Loading document…</div>}
    </div>
  );
}
